import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Store, LogOut, Plus, Trash2, ExternalLink, Eye, UtensilsCrossed, Image as ImageIcon, Clock, Upload } from "lucide-react";

export default function RestaurantDashboard() {
  const navigate = useNavigate();
  const { owner, loading, logoutOwner } = useAuth();
  const { t } = useLang();
  const [rest, setRest] = useState(null);
  const [profile, setProfile] = useState({});
  const [item, setItem] = useState({ name: "", description: "", price: "", category: "Plats", image: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState("");

  const uploadImage = async (fileList, onDone) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("dash.imageTooLarge")); return; }
    setUploading(onDone.key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone.set(`${API}/files/${res.data.path}`);
      toast.success(t("dash.imageUploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toast.error"));
    } finally {
      setUploading("");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!owner) { navigate("/restaurant"); return; }
    api.get("/my/restaurant").then((r) => {
      setRest(r.data);
      setProfile(r.data);
    });
  }, [owner, loading, navigate]);

  const setP = (k) => (e) => { const v = e.target.value; setProfile((prev) => ({ ...prev, [k]: v })); };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await api.put("/my/restaurant", {
        name: profile.name, cuisine: profile.cuisine, description: profile.description,
        image: profile.image, address: profile.address, city: profile.city,
        delivery_time: profile.delivery_time, price_range: profile.price_range,
        doordash_link: profile.doordash_link,
        ubereats_link: profile.ubereats_link, owner_name: profile.owner_name,
        phone: profile.phone, email: profile.email,
      });
      setRest(res.data);
      toast.success(t("toast.profileUpdated"));
    } catch {
      toast.error(t("toast.error"));
    } finally {
      setSavingProfile(false);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/my/restaurant/menu", {
        name: item.name, description: item.description,
        price: parseFloat(item.price) || 0, category: item.category || "Plats",
        image: item.image,
      });
      setRest(res.data);
      setItem({ name: "", description: "", price: "", category: "Plats", image: "" });
      toast.success(t("toast.itemAdded"));
    } catch {
      toast.error(t("toast.error"));
    }
  };

  const deleteItem = async (itemId) => {
    const res = await api.delete(`/my/restaurant/menu/${itemId}`);
    setRest(res.data);
    toast.success(t("toast.itemDeleted"));
  };

  if (loading || !rest) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] font-mono text-[#5C5C5C]">{t("grid.loading")}</div>;
  }

  const inputCls = "w-full h-11 px-4 bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg outline-none focus:border-[#E63946] transition-colors text-sm";

  return (
    <div className="flex min-h-screen bg-[#FDFBF7]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#E5E0D8] bg-white hidden md:flex flex-col">
        <div className="h-20 flex items-center gap-2 px-6 border-b border-[#E5E0D8]">
          <span className="w-8 h-8 rounded-full bg-[#E63946] flex items-center justify-center">
            <UtensilsCrossed className="w-4 h-4 text-white" />
          </span>
          <span className="text-xl font-display font-black tracking-tighter">Vytali</span>
        </div>
        <div className="p-6 flex-1">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C5C5C] font-mono mb-2">{t("dash.restaurant")}</p>
          <p className="font-bold text-[#2B2B2B]">{rest.name}</p>
          <p className="text-sm text-[#5C5C5C] font-mono">{owner?.email}</p>
        </div>
        <div className="p-6 border-t border-[#E5E0D8] space-y-2">
          <button data-testid="dash-view-public" onClick={() => window.open(`/restaurant/${rest.id}`, "_blank", "noopener,noreferrer")}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-[#2B2B2B] hover:bg-[#F4EFE6] transition-colors">
            <Eye className="w-4 h-4" /> {t("dash.viewPage")}
          </button>
          <button data-testid="dash-logout" onClick={() => { logoutOwner(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-[#E63946] hover:bg-[#F4EFE6] transition-colors">
            <LogOut className="w-4 h-4" /> {t("dash.logout")}
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-black tracking-tighter text-[#2B2B2B]">{t("dash.title")}</h1>
            <p className="text-[#5C5C5C] mt-1">{t("dash.subtitle")}</p>
          </div>
          <button data-testid="mobile-logout" onClick={() => { logoutOwner(); navigate("/"); }}
            className="md:hidden p-2 rounded-full hover:bg-[#F4EFE6]"><LogOut className="w-5 h-5" /></button>
        </div>

        {rest.status === "pending" && (
          <div data-testid="pending-validation-banner" className="mb-8 flex items-start gap-3 bg-[#FFF6E9] border border-[#F4A261] rounded-xl p-4">
            <Clock className="w-5 h-5 text-[#C97A1A] shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-[#2B2B2B]">{t("dash.pendingTitle")}</p>
              <p className="text-sm text-[#5C5C5C] mt-0.5">{t("dash.pendingDesc")}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile */}
          <section className="bg-white border border-[#E5E0D8] rounded-xl p-6">
            <h2 className="flex items-center gap-2 text-xl font-display font-bold mb-5"><Store className="w-5 h-5 text-[#E63946]" /> {t("dash.profile")}</h2>
            <div className="space-y-3">
              <input data-testid="profile-name" className={inputCls} value={profile.name || ""} onChange={setP("name")} placeholder={t("dash.name")} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="profile-owner-name" className={inputCls} value={profile.owner_name || ""} onChange={setP("owner_name")} placeholder={t("dash.ownerName")} />
                <input data-testid="profile-phone" className={inputCls} value={profile.phone || ""} onChange={setP("phone")} placeholder={t("dash.phone")} />
              </div>
              <input data-testid="profile-email" type="email" className={inputCls} value={profile.email || ""} onChange={setP("email")} placeholder={t("dash.email")} />
              <input data-testid="profile-cuisine" className={inputCls} value={profile.cuisine || ""} onChange={setP("cuisine")} placeholder={t("dash.cuisine")} />
              <textarea data-testid="profile-description" className={inputCls + " h-24 py-2 resize-none"} value={profile.description || ""} onChange={setP("description")} placeholder={t("dash.description")} />
              <div className="flex items-center gap-3">
                <input data-testid="profile-image" className={inputCls} value={profile.image || ""} onChange={setP("image")} placeholder={t("dash.imageUrl")} />
                <label data-testid="profile-image-upload-btn" className="shrink-0 h-11 px-3 flex items-center gap-1.5 rounded-lg border border-[#E5E0D8] bg-white text-[#2B2B2B] text-sm font-bold cursor-pointer hover:bg-[#F4EFE6] transition-colors">
                  <Upload className="w-4 h-4" /> {uploading === "profile" ? "…" : t("dash.upload")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files, { key: "profile", set: (url) => setProfile((p) => ({ ...p, image: url })) })} />
                </label>
              </div>
              <input data-testid="profile-address" className={inputCls} value={profile.address || ""} onChange={setP("address")} placeholder={t("dash.address")} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="profile-city" className={inputCls} value={profile.city || ""} onChange={setP("city")} placeholder={t("dash.city")} />
                <input data-testid="profile-delivery" className={inputCls} value={profile.delivery_time || ""} onChange={setP("delivery_time")} placeholder={t("dash.deliveryTime")} />
              </div>
              <input data-testid="profile-price" className={inputCls} value={profile.price_range || ""} onChange={setP("price_range")} placeholder={t("dash.priceRangeCad")} />
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5C5C] font-mono flex items-center gap-1 mb-1">
                  <ExternalLink className="w-3 h-3" /> {t("dash.doordash")}
                </label>
                <input data-testid="profile-doordash" className={inputCls} value={profile.doordash_link || ""} onChange={setP("doordash_link")} placeholder="https://www.doordash.com/..." />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5C5C] font-mono flex items-center gap-1 mb-1">
                  <ExternalLink className="w-3 h-3" /> {t("dash.ubereats")}
                </label>
                <input data-testid="profile-ubereats" className={inputCls} value={profile.ubereats_link || ""} onChange={setP("ubereats_link")} placeholder="https://www.ubereats.com/..." />
              </div>
              <button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile}
                className="w-full h-11 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50">
                {savingProfile ? "…" : t("dash.save")}
              </button>
            </div>
          </section>

          {/* Menu */}
          <section className="bg-white border border-[#E5E0D8] rounded-xl p-6">
            <h2 className="text-xl font-display font-bold mb-5">{t("dash.menu")} ({rest.menu?.length || 0})</h2>
            <form onSubmit={addItem} className="space-y-3 mb-6 pb-6 border-b border-[#E5E0D8]">
              <input data-testid="item-name" required className={inputCls} value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder={t("dash.itemName")} />
              <input data-testid="item-description" className={inputCls} value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} placeholder={t("dash.itemDescription")} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="item-price" type="number" step="0.01" required className={inputCls} value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} placeholder={t("dash.itemPriceCad")} />
                <input data-testid="item-category" className={inputCls} value={item.category} onChange={(e) => setItem({ ...item, category: e.target.value })} placeholder={t("dash.itemCategory")} />
              </div>
              <div className="flex items-center gap-3">
                <input data-testid="item-image" className={inputCls} value={item.image} onChange={(e) => setItem({ ...item, image: e.target.value })} placeholder={t("dash.itemImage")} />
                <label data-testid="item-image-upload-btn" className="shrink-0 h-11 px-3 flex items-center gap-1.5 rounded-lg border border-[#E5E0D8] bg-white text-[#2B2B2B] text-sm font-bold cursor-pointer hover:bg-[#F4EFE6] transition-colors">
                  <Upload className="w-4 h-4" /> {uploading === "item" ? "…" : t("dash.upload")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files, { key: "item", set: (url) => setItem((p) => ({ ...p, image: url })) })} />
                </label>
                {item.image ? (
                  <img src={item.image} alt="" className="w-11 h-11 rounded-lg object-cover border border-[#E5E0D8] shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="w-11 h-11 rounded-lg bg-[#F4EFE6] flex items-center justify-center shrink-0"><ImageIcon className="w-5 h-5 text-[#9C9C9C]" /></span>
                )}
              </div>
              <button data-testid="add-item-btn" type="submit"
                className="w-full h-11 flex items-center justify-center gap-2 rounded-full bg-[#2B2B2B] text-white font-bold hover:scale-[1.02] transition-transform">
                <Plus className="w-4 h-4" /> {t("dash.addItem")}
              </button>
            </form>

            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {(rest.menu || []).length === 0 ? (
                <p className="text-sm text-[#5C5C5C] font-mono text-center py-6">{t("dash.noItems")}</p>
              ) : (
                rest.menu.map((m) => (
                  <div key={m.id} data-testid={`dash-menu-item-${m.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E5E0D8]">
                    <div className="flex items-center gap-3 min-w-0">
                      {m.image ? (
                        <img src={m.image} alt={m.name} className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span className="w-12 h-12 rounded-lg bg-[#F4EFE6] flex items-center justify-center shrink-0"><ImageIcon className="w-5 h-5 text-[#9C9C9C]" /></span>
                      )}
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-[#2B2B2B] truncate">{m.name} <span className="font-mono text-[#E63946]">· {Number(m.price).toFixed(2)} $</span></p>
                        <p className="text-xs text-[#5C5C5C] font-mono">{m.category}</p>
                      </div>
                    </div>
                    <button data-testid={`delete-item-${m.id}`} onClick={() => deleteItem(m.id)}
                      className="p-2 rounded-lg text-[#E63946] hover:bg-[#F4EFE6] transition-colors shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
